import { ReactElement } from "react";
import { HubDetailsLayout, MediaContentModel } from "../../HubDetailsLayout";
import {
	DateString,
	Icon,
	TagList,
	TagModel,
	PipeSeparator
} from "client/Components";
import {
	ContentViewer,
	ContentViewerEmptyBlockSettings,
	HubItemLayoutContentViewerModel
} from "../../Builder/Viewer/ContentViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { LinkedDocumentsViewer } from "../../LinkedDocumentsViewer";
import { localize } from "client_legacy/ndaw/culture";
import {
	ContentTitle,
	HubItemNameOnlyCategory,
	isFullCategoryModel,
	resolveTitle,
	resolveTitleKind
} from "../../Content/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { ActiveFilter, ImageUrlWithAttribution } from "../../common";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { LinkedDocumentPreviewModel, LinkedDocumentsPreview } from "../../LinkedDocumentsPreview";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import { KnowledgeItemStatisticsModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { History } from "../../History";
import { KnowledgeItemActions } from "./Actions/KnowledgeItemActions";

export type KnowledgeItemContentDataProps = ConditionalProps<
	{
		title: ContentTitle;
		category: HubItemCategoryModel | HubItemNameOnlyCategory | undefined;
		publicationDate: Date | string;
		createdBy: UserIdentityModel | null;
		media: MediaContentModel;
		layout: HubItemLayoutContentViewerModel;
		tags: readonly TagModel<number>[];
		isEditable: boolean;
		isPinned: boolean;
		modifiedDate: Date | string;
		modifiedBy: UserIdentityModel | null;
		emptyBlockSettings?: ContentViewerEmptyBlockSettings;
		shouldNotRenderEmptyFooter?: boolean;
		areCommentsEnabled: boolean;
	},
	"hasData"
>;

export type KnowledgeItemContentModeProps = {
	mode: "full";
	activeFilter: ActiveFilter | null;
	onBack: () => void;
	onEdit: () => void;
	onSelectCategory: (categoryId: number) => void;
	onSelectAuthor: (authorId: number, authorName: string) => void;
	onTagClick: (tag: number | null) => void;
} | {
	mode: "details-only";
	onSelectCategory?: (categoryId: number) => void;
};

export type KnowledgeItemContentLinkedDocumentsModeProps =
	| {
		linkedDocumentsMode: "preview";
		linkedDocuments: readonly LinkedDocumentPreviewModel[];
		onChangeIsLoading?: undefined;
	}
	| {
		linkedDocumentsMode: "view";
		linkedDocuments: readonly HubLinkedDocumentViewModel[];
		onChangeIsLoading: (isLoading: boolean) => void;
	};

export type KnowledgeItemContentStatisticsProps = ConditionalProps<
	{
		statistics: KnowledgeItemStatisticsModel;
		onOpenViewersDialog?: () => void;
		onOpenReactionsDialog: () => void;
		onStatisticsRetrieved: (statistics: KnowledgeItemStatisticsModel) => void;
	},
	"hasStatistics"
>;

export type KnowledgeItemContentInteractiveProps = ConditionalProps<
	{
		id: number;
	},
	"isInteractive"
>;

export type KnowledgeItemContentProps = {
	timezone: TimeZone;
	onViewImage?: (image: ImageUrlWithAttribution) => void;
}
	& KnowledgeItemContentDataProps
	& KnowledgeItemContentModeProps
	& KnowledgeItemContentLinkedDocumentsModeProps
	& KnowledgeItemContentInteractiveProps
	& KnowledgeItemContentStatisticsProps;

export const KnowledgeItemContent = (props: KnowledgeItemContentProps): ReactElement => {
	const {
		hasData,
		title,
		category,
		publicationDate,
		createdBy,
		media,
		layout,
		tags,
		isEditable,
		isPinned,
		modifiedDate,
		modifiedBy,
		emptyBlockSettings,
		onSelectCategory,
		linkedDocumentsMode,
		linkedDocuments,
		onChangeIsLoading,
		hasStatistics,
		statistics,
		onOpenViewersDialog,
		onOpenReactionsDialog,
		isInteractive,
		id,
		timezone,
		shouldNotRenderEmptyFooter = false,
		onViewImage,
		areCommentsEnabled,
		onStatisticsRetrieved
	} = props;

	const renderAuthor = (author: UserIdentityModel): ReactElement | null => {
		const {
			computedDisplayName
		} = getComputedDisplayName({
			firstName: author.firstName,
			lastName: author.lastName
		});

		return (
			<HubDetailsLayout.SubtitleLink
				trackingId="author"
				onClick={
					props.mode === "full"
						? () => props.onSelectAuthor(author.userId, computedDisplayName)
						: undefined
				}
				tooltip={computedDisplayName}
			>
				{computedDisplayName}
			</HubDetailsLayout.SubtitleLink>
		);
	};

	const renderLinkedDocuments = (): ReactElement | null => {
		const count: number = linkedDocuments.length;

		if (count === 0) {
			return null;
		}

		switch (linkedDocumentsMode) {
			case "preview":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsPreview
							items={linkedDocuments}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			case "view":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsViewer
							items={linkedDocuments}
							onChangeIsLoading={onChangeIsLoading}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			default:
				never(linkedDocumentsMode);
		}
	};

	return (
		<HubDetailsLayout>
			{props.mode === "full"
				? (
					<>
						<HubDetailsLayout.BackButton
							activeFilter={props.activeFilter}
							onBack={props.onBack}
						/>
						{hasData && (
							<HubDetailsLayout.Title
								onEdit={isEditable ? props.onEdit : undefined}
								text={resolveTitle(title)}
								kind={resolveTitleKind(title)}
								isPinned={isPinned}
							/>
						)}
					</>
				)
				: hasData && (
					<HubDetailsLayout.SimpleTitle
						text={resolveTitle(title)}
						kind={resolveTitleKind(title)}
						isPinned={isPinned}
					/>
				)
			}
			{hasData && (
				<>
					<HubDetailsLayout.Subtitle>
						<Icon value="pen" />

						<HubDetailsLayout.SubtitleText shouldNotShrink>
							{isValidDate(modifiedDate)
								? (
									<DateString
										value={modifiedDate}
										format="RelativeTimestamp"
										timezone={timezone}
									/>
								)
								: localize("_NoValidInput")
							}
						</HubDetailsLayout.SubtitleText>

						{createdBy && (
							<>
								<PipeSeparator />
								{renderAuthor(createdBy)}
							</>
						)}

						{category && (
							<>
								<PipeSeparator />
								<HubDetailsLayout.SubtitleLink
									trackingId="category"
									onClick={
										onSelectCategory && isFullCategoryModel(category)
											? () => onSelectCategory(category.id)
											: undefined
									}
									tooltip={category.name}
								>
									{category.name}
								</HubDetailsLayout.SubtitleLink>
							</>
						)}
					</HubDetailsLayout.Subtitle>

					<HubDetailsLayout.Media
						media={media}
						hubItemKind={HubItemKind.KnowledgeItem}
						onViewImage={onViewImage}
					/>

					<HubDetailsLayout.Content>
						<ContentViewer
							hubItemKind={HubItemKind.KnowledgeItem}
							layout={layout}
							onViewImage={onViewImage}
							emptyBlockSettings={emptyBlockSettings}
						/>
						<History>
							<History.Date
								dateName="_Published"
								date={publicationDate}
								user={createdBy}
								onClickAuthor={
									props.mode === "full"
										? props.onSelectAuthor
										: undefined
								}
								trackingId="user-publisher"
								timezone={timezone}
							/>
							<History.Separator />
							<History.Date
								dateName="_LastUpdated"
								date={modifiedDate}
								user={modifiedBy}
								onClickAuthor={
									props.mode === "full"
										? props.onSelectAuthor
										: undefined
								}
								trackingId="user-modifier"
								timezone={timezone}
							/>
						</History>
					</HubDetailsLayout.Content>
					{renderLinkedDocuments()}
					{(!shouldNotRenderEmptyFooter
						|| hasStatistics
						|| tags.length > 0)
						&& (
							<HubDetailsLayout.Footer
								stats={hasStatistics
									? {
										reactions: {
											value: statistics.reactionCounters,
											onClick: statistics.reactionCounters.length > 0
												? onOpenReactionsDialog
												: undefined,
										},
										views: {
											value: statistics.viewsCount,
											onClick: statistics.viewsCount > 0
												? onOpenViewersDialog
												: undefined
										}
									}
									: undefined
								}
								rightSide={(
									<TagList
										trackingId="tag-list"
										tags={tags}
										onTagClick={
											props.mode === "full"
												? props.onTagClick
												: undefined
										}
										alignment="right"
										showMoreTagsColor="secondary"
									/>
								)}
							>
								{isInteractive && hasStatistics && (
									<KnowledgeItemActions
										knowledgeItemId={id}
										statistics={statistics}
										areCommentsEnabled={areCommentsEnabled}
										onStatisticsRetrieved={onStatisticsRetrieved}
									/>
								)}
							</HubDetailsLayout.Footer>
						)
					}
				</>
			)}
		</HubDetailsLayout>
	);
};
KnowledgeItemContent.displayName = "KnowledgeItemContent";
